import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LaunchProvider, useLaunch, marketplaceOpen } from "@/context/LaunchContext";
import { CompareProvider } from "@/context/CompareContext";
import ImpersonationBanner from "@/components/admin/ImpersonationBanner";

// Critical initial-paint modules — kept in the main chunk so the public
// homepage paints without waiting on a secondary chunk.
import Home from "@/pages/Home";
import PrivateShell from "@/components/PrivateShell";
import PublicShell from "@/components/public/PublicShell";
import NotFound from "@/pages/public/NotFound";
import AnalyticsBoundary from "@/components/AnalyticsBoundary";
import ScrollToTop from "@/components/ScrollToTop";
import PageFallback from "@/components/PageFallback";
// Vanturis Passport — Phase 8 standalone product routes (host-aware)
import { passportRoutes } from "@/passport/routes";
import { isPassportHost } from "@/passport/product";
import { activeVerticalHost } from "@/lib/verticalHost";

// ---- Route-level code splitting (Performance P1B) --------------------------
// Every route-exclusive page below is lazy-loaded so opening the public
// homepage no longer ships Admin / Data-Management / Inspector / Create-Listing
// / Passport-management code in the initial JS bundle. Route URLs, auth guards
// and redirects are unchanged — a dynamic import is NOT authorization. Suspense
// boundaries live INSIDE the shells (see PrivateShell/PublicShell) plus one
// neutral outer boundary, so navigation never tears down the Vanturis frame.

// Public (coming-soon) + marketing pages
const ComingSoonHome = lazy(() => import("@/pages/public/ComingSoonHome"));
const LuxuryMotorhomesPreview = lazy(() => import("@/pages/public/LuxuryMotorhomesPreview"));
const TouringCollectionPreview = lazy(() => import("@/pages/public/TouringCollectionPreview"));
const TourTrailersPreview = lazy(() => import("@/pages/public/TourTrailersPreview"));
const AuctionsPreview = lazy(() => import("@/pages/public/AuctionsPreview"));
const PremiumClassAPreview = lazy(() => import("@/pages/public/PremiumClassAPreview"));
const PremiumSuperCPreview = lazy(() => import("@/pages/public/PremiumSuperCPreview"));
const OverlandVehiclesPreview = lazy(() => import("@/pages/public/OverlandVehiclesPreview"));
const OverlandTrailersPreview = lazy(() => import("@/pages/public/OverlandTrailersPreview"));
const EuropeanCollectionPreview = lazy(() => import("@/pages/public/EuropeanCollectionPreview"));
const CollectionsIndex = lazy(() => import("@/pages/public/CollectionsIndex"));
const VanturisTrust = lazy(() => import("@/pages/public/VanturisTrust"));
const VanturisStandards = lazy(() => import("@/pages/public/VanturisStandards"));
const ConciergePreview = lazy(() => import("@/pages/public/ConciergePreview"));
const AboutPage = lazy(() => import("@/pages/public/AboutPage"));
const ContactPage = lazy(() => import("@/pages/public/ContactPage"));
const EarlyAccessPage = lazy(() => import("@/pages/public/EarlyAccessPage"));
const PrevostMotorhomesCategory = lazy(() => import("@/pages/public/PrevostMotorhomesCategory"));
const WhyVanturis = lazy(() => import("@/pages/public/WhyVanturis"));
const InternalAccess = lazy(() => import("@/pages/InternalAccess"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));

// Round B: Public marketplace + Legal pages
const PublicMarketplace = lazy(() => import("@/pages/public/PublicMarketplace"));
const PublicListingDetail = lazy(() => import("@/pages/public/PublicListingDetail"));
const PrivateAviation = lazy(() => import("@/pages/public/PrivateAviation"));
const LuxuryMarine = lazy(() => import("@/pages/public/LuxuryMarine"));
const LegalTerms = lazy(() => import("@/pages/public/legal/LegalTerms"));
const LegalPrivacy = lazy(() => import("@/pages/public/legal/LegalPrivacy"));
const LegalListingDisclaimer = lazy(() => import("@/pages/public/legal/LegalListingDisclaimer"));

// Private (marketplace) pages
const Browse = lazy(() => import("@/pages/Browse"));
const ListingDetail = lazy(() => import("@/pages/ListingDetail"));
const Register = lazy(() => import("@/pages/Register"));
const Login = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const CreateListing = lazy(() => import("@/pages/CreateListing"));
const TrustProducts = lazy(() => import("@/pages/TrustProducts"));
const SellPage = lazy(() => import("@/pages/SellPage"));
const Passport = lazy(() => import("@/pages/Passport"));
const Partners = lazy(() => import("@/pages/Partners"));
// Phase 9 — Vanturis Partner Network (Become a Partner)
const BecomeAPartner = lazy(() => import("@/pages/partners/BecomeAPartner"));
const PartnerApply = lazy(() => import("@/pages/partners/PartnerApply"));
const PartnerCategoryLanding = lazy(() => import("@/pages/partners/PartnerCategoryLanding"));
const PartnerPortal = lazy(() => import("@/pages/partners/PartnerPortal"));
const Compare = lazy(() => import("@/pages/Compare"));
const InspectorPortal = lazy(() => import("@/pages/InspectorPortal"));
const InspectorDesignV2 = lazy(() => import("@/pages/preview/InspectorDesignV2"));
const InspectorV2Preview = lazy(() => import("@/pages/preview/InspectorV2Preview"));
const TireTreadExample = lazy(() => import("@/pages/preview/TireTreadExample"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const CheckoutCancel = lazy(() => import("@/pages/CheckoutCancel"));
const Concierge = lazy(() => import("@/pages/Concierge"));
const AccountSecurity = lazy(() => import("@/pages/AccountSecurity"));
const ListingWorkspace = lazy(() => import("@/pages/ListingWorkspace"));
const AdminPortal = lazy(() => import("@/pages/AdminPortal"));
const PassportAdminPortal = lazy(() => import("@/pages/PassportAdminPortal"));
const ActivationPage = lazy(() => import("@/pages/ActivationPage"));

/** Root route selects public landing OR the private marketplace Home
 *  depending on the effective launch mode + user.
 *
 *  Iter 125: reversed the "safe default" of the initial paint.  Since
 *  the site is permanently in ``public_launch`` for real users, we now
 *  render the marketplace Home immediately (no flash of the retired
 *  coming-soon interstitial while ``useLaunch()`` resolves).  Only when
 *  the authoritative config explicitly comes back with
 *  ``launch_mode === "coming_soon"`` AND the caller is not an approved
 *  internal / preview user do we downgrade back to ``ComingSoonHome``.
 *  This is still one-way safe: an admin who flips the DB back to
 *  ``coming_soon`` sees the interstitial appear once the config lands,
 *  which is the intended admin experience.
 */
function RootRoute() {
  const { user, loading: authLoading } = useAuth();
  const { config, loading: launchLoading } = useLaunch();
  const ready = !authLoading && !launchLoading;
  // While config is loading we optimistically render the public
  // marketplace shell — no coming-soon flash.
  if (!ready) {
    return (
      <PrivateShell>
        <Home />
      </PrivateShell>
    );
  }
  // Authoritative decision after both auth + config have resolved.
  const open = marketplaceOpen(config, user);
  if (!open) return <ComingSoonHome />;
  return (
    <PrivateShell>
      <Home />
    </PrivateShell>
  );
}

/** /register — public registration is disabled during coming_soon.
 *  In private_preview + public_registration_enabled, the standard register page is shown.
 *  Otherwise redirects to /. */
function RegisterRoute() {
  const { config, loading } = useLaunch();
  if (loading) return null;
  if (!config?.public_registration_enabled) return <Navigate to="/" replace />;
  return (
    <PrivateShell>
      <Register />
    </PrivateShell>
  );
}

/** /login — public sign-in is only exposed in public_launch (or when
 *  public_login_enabled is explicitly on during private_preview).  In
 *  coming_soon, all sign-in traffic is funneled through the private
 *  /internal-access route.  This route was previously an unconditional
 *  redirect to /internal-access, which broke launch cutover — after the
 *  Mongo launch_mode flipped to public_launch the nav's "Sign In" link
 *  still bounced buyers into the "Approved team members only" screen.
 */
function LoginRoute() {
  const { config, loading } = useLaunch();
  if (loading) return null;
  const mode = config?.launch_mode;
  const publicLoginOpen = mode === "public_launch" || (mode === "private_preview" && config?.public_login_enabled);
  if (!publicLoginOpen) return <Navigate to="/internal-access" replace />;
  return (
    <PublicShell>
      <Login />
    </PublicShell>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-ivory text-ink">
      <HelmetProvider>
        <BrowserRouter>
          <LaunchProvider>
            <AuthProvider>
              <CompareProvider>
                <AnalyticsBoundary />
                <ScrollToTop />
                <ImpersonationBanner />
                <Suspense fallback={<div className="min-h-screen bg-ivory" aria-hidden="true" data-testid="route-fallback" />}>
                <Routes>
                {isPassportHost() ? (
                  /* Passport host context (future passport.vanturis.com):
                     ONLY the Passport product mounts — Marketplace routes
                     are not served on the Passport hostname. */
                  <>
                    {passportRoutes("")}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </>
                ) : activeVerticalHost() ? (
                  /* Companion vertical subdomain context (future
                     aviation.vanturis.com / marine.vanturis.com): the SAME
                     vertical page mounts at the root. Canonical paths keep
                     working; everything else routes home. Config-driven via
                     REACT_APP_AVIATION_HOSTNAMES / REACT_APP_MARINE_HOSTNAMES
                     (empty by default → this branch is inert). */
                  <>
                    <Route
                      path="/"
                      element={activeVerticalHost() === "private-aviation" ? <PrivateAviation /> : <LuxuryMarine />}
                    />
                    <Route path="/marketplace/private-aviation" element={<PrivateAviation />} />
                    <Route path="/marketplace/luxury-marine" element={<LuxuryMarine />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </>
                ) : (
                <>
                {/* Public (always available) */}
                <Route path="/" element={<RootRoute />} />
                <Route path="/luxury-motorhomes-preview" element={<LuxuryMotorhomesPreview />} />
                <Route path="/ultra-luxury-motorcoaches" element={<LuxuryMotorhomesPreview />} />
                <Route path="/touring-collection-preview" element={<TouringCollectionPreview />} />
                <Route path="/touring-collection" element={<TouringCollectionPreview />} />
                <Route path="/tour-trailers-preview" element={<TourTrailersPreview />} />
                <Route path="/specialty-trailers" element={<TourTrailersPreview />} />
                <Route path="/premium-class-a" element={<PremiumClassAPreview />} />
                <Route path="/premium-super-c" element={<PremiumSuperCPreview />} />
                <Route path="/overland-vehicles" element={<OverlandVehiclesPreview />} />
                <Route path="/overland-trailers" element={<OverlandTrailersPreview />} />
                <Route path="/european-collection" element={<EuropeanCollectionPreview />} />
                <Route path="/auctions-preview" element={<AuctionsPreview />} />
                <Route path="/collections" element={<CollectionsIndex />} />
                <Route path="/vanturis-trust" element={<VanturisTrust />} />
                <Route path="/vanturis-standards" element={<VanturisStandards />} />
                <Route path="/concierge-preview" element={<ConciergePreview />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                {/* Thin /privacy + /terms stubs 301 to the full canonical /legal/* pages */}
                <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
                <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
                <Route path="/early-access" element={<EarlyAccessPage />} />
                <Route path="/prevost-motorhomes" element={<PrevostMotorhomesCategory />} />
                <Route path="/why-list-with-vanturis" element={<WhyVanturis />} />
                <Route path="/why-vanturis" element={<Navigate to="/why-list-with-vanturis" replace />} />

                {/* Round B — Public marketplace + Listing Detail + Legal pages */}
                <Route path="/marketplace" element={<PublicMarketplace />} />
                <Route path="/marketplace/private-aviation" element={<PrivateAviation />} />
                <Route path="/marketplace/luxury-marine" element={<LuxuryMarine />} />
                <Route path="/marketplace/:slug" element={<PublicListingDetail />} />
                <Route path="/legal/terms" element={<LegalTerms />} />
                <Route path="/legal/privacy" element={<LegalPrivacy />} />
                <Route path="/legal/listing-disclaimer" element={<LegalListingDisclaimer />} />

                {/* Internal sign-in (undocumented) */}
                <Route path="/internal-access" element={<InternalAccess />} />
                {/* Launch-mode-aware public login (falls through to /internal-access
                    when the site is still gated). */}
                <Route path="/login" element={<LoginRoute />} />

                {/* Phase 2 account invitation activation — public, no gate */}
                <Route path="/account/activate/:token" element={<ActivationPage />} />

                {/* Launch Blocker Sprint 1 — public account recovery & verification */}
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/resend-verification" element={<VerifyEmail />} />

                {/* Public registration gate */}
                <Route path="/register" element={<RegisterRoute />} />

                {/* Private / marketplace routes — every one wrapped in LaunchGate via PrivateShell */}
                <Route path="/browse"           element={<PrivateShell><Browse /></PrivateShell>} />
                <Route path="/coaches"          element={<PrivateShell><Browse /></PrivateShell>} />
                <Route path="/listing/:id"      element={<PublicShell><ListingDetail /></PublicShell>} />
                <Route path="/dashboard/*"      element={<PrivateShell requireAuth><Dashboard /></PrivateShell>} />
                <Route path="/admin/*"          element={<PrivateShell requireAuth><AdminPortal /></PrivateShell>} />
                {/* Vanturis Passport — dedicated internal Admin portal.
                    Distinct product experience over the SAME Vanturis auth
                    and the existing Passport admin panels/endpoints. Static
                    segment; ranks above the customer /passport/:vin route. */}
                <Route path="/passport/admin"   element={<PrivateShell requireAuth><PassportAdminPortal /></PrivateShell>} />
                <Route path="/sell"             element={<PrivateShell><SellPage /></PrivateShell>} />
                <Route path="/pricing"          element={<PrivateShell><Pricing /></PrivateShell>} />
                <Route path="/list-coach"       element={<PrivateShell requireAuth><CreateListing /></PrivateShell>} />
                <Route path="/trust"            element={<PrivateShell><TrustProducts /></PrivateShell>} />
                <Route path="/passport/:vin"    element={<PrivateShell><Passport /></PrivateShell>} />
                {/* Vanturis Passport — Phase 8 standalone product experience.
                    Mounted at /passport in path-mode; the same route table
                    mounts at "/" on a configured Passport hostname.  The
                    legacy /passport/owner/:assetId route now redirects to
                    /passport/assets/:assetId inside passportRoutes(). */}
                {passportRoutes("/passport")}
                <Route path="/partners"         element={<PrivateShell><Partners /></PrivateShell>} />
                {/* Phase 9 — Vanturis Partner Network: acquisition landing, dynamic
                    application, category campaign landings, partner portal. */}
                <Route path="/become-a-partner"                element={<PrivateShell><BecomeAPartner /></PrivateShell>} />
                <Route path="/become-a-partner/apply"          element={<PrivateShell><PartnerApply /></PrivateShell>} />
                <Route path="/become-a-partner/portal"         element={<PrivateShell requireAuth><PartnerPortal /></PrivateShell>} />
                <Route path="/become-a-partner/:categoryCode"  element={<PrivateShell><PartnerCategoryLanding /></PrivateShell>} />
                <Route path="/compare"          element={<PrivateShell><Compare /></PrivateShell>} />
                <Route path="/concierge"        element={<PrivateShell><Concierge /></PrivateShell>} />
                {/* Operational Inspector entry now opens the APPROVED detailed Inspector v2 workflow
                    (tire positions/tread/PSI/DOT, damage map, findings, finalize -> Passport bridge). */}
                <Route path="/inspector"        element={<PrivateShell requireAuth><InspectorV2Preview /></PrivateShell>} />
                <Route path="/inspector-v2"     element={<PrivateShell requireAuth><InspectorV2Preview /></PrivateShell>} />
                {/* Legacy v1 pass/concern/fail checklist — retained for reference, no longer the entry point. */}
                <Route path="/inspector-legacy" element={<PrivateShell requireAuth><InspectorPortal /></PrivateShell>} />
                {/* Internal-only design preview — Inspector v2 wireframes. Not linked from public nav. */}
                <Route path="/preview/inspector-design-v2" element={<PrivateShell requireAuth><InspectorDesignV2 /></PrivateShell>} />
                {/* Inspector v2 · P0 Preview surface — internal + inspector only, feature-flagged. */}
                <Route path="/preview/inspector-v2" element={<PrivateShell requireAuth><InspectorV2Preview /></PrivateShell>} />
                {/* Tire Tread Depth & Position Map — Preview example (synthetic data). */}
                <Route path="/preview/tire-tread-example" element={<PrivateShell><TireTreadExample /></PrivateShell>} />
                <Route path="/account/security" element={<PrivateShell requireAuth><AccountSecurity /></PrivateShell>} />
                <Route path="/account"          element={<Navigate to="/account/security" replace />} />
                <Route path="/workspace"        element={<PrivateShell requireAuth><ListingWorkspace /></PrivateShell>} />
                <Route path="/checkout/success" element={<PrivateShell><CheckoutSuccess /></PrivateShell>} />
                <Route path="/checkout/cancel"  element={<PrivateShell><CheckoutCancel /></PrivateShell>} />

                {/* Anything else → true 404 (noindex) instead of a soft-404 homepage clone */}
                <Route path="*" element={<NotFound />} />
                </>
                )}
              </Routes>
                </Suspense>
              <Toaster position="top-center" toastOptions={{ style: { fontFamily: "'Source Sans 3', sans-serif" } }} />
              </CompareProvider>
            </AuthProvider>
          </LaunchProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
